import { COLORS, TYPOGRAPHY } from '@app/constants';
import { CloseCircle, Search } from '@app/ui/iconComponents';
import React, { FC, useState, ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';

interface Props {
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  isAllowEmptySearch?: boolean;
  onSearch: (searchValue: string) => void;
  onReset?: () => void;
  controlledChange?: (str: string) => void;
  selector?: (state: any) => any;
  onChange?: (str: string) => void;
  initValue?: string;
}

const SearchInput: FC<Props> = ({
  placeholder = 'Поиск',
  isDisabled = false,
  isAllowEmptySearch = false,
  onSearch,
  onReset,
  className,
  onChange,
  initValue,
}) => {
  const [searchValue, setSearchValue] = useState(initValue || '');
  const [isFocus, setIsFocus] = useState(true);

  const handleChangeSearchValue = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    setSearchValue(value);
    onChange?.(value);
  };

  const handleChangeFocus = () => {
    setIsFocus(!isFocus);
  };

  const clearSearchValue = () => {
    setSearchValue('');
    onChange?.('');
    onReset?.();
  };

  const search = () => {
    if (isAllowEmptySearch || searchValue) {
      onSearch(searchValue);
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      search();
    }
  };

  return (
    <Root
      onFocus={handleChangeFocus}
      onBlur={handleChangeFocus}
      className={className}
    >
      <SearchButton onClick={search}>
        <Search color={isDisabled ? COLORS.grayscale200 : COLORS.grayscale400} />
      </SearchButton>
      <Input
        placeholder={placeholder}
        disabled={isDisabled}
        value={searchValue}
        onChange={handleChangeSearchValue}
        onKeyPress={handleKeyPress}
        enterKeyHint="search"
      />
      {searchValue && (
        <ClearButton onClick={clearSearchValue}>
          <CloseCircle color={isFocus ? COLORS.grayscale200 : COLORS.primary300} />
        </ClearButton>
      )}
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchButton = styled.button`
  position: absolute;
  left: 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 40px 11px;
  ${TYPOGRAPHY.body1Regular14}
  color: ${COLORS.grayscale700};
  border-bottom: 1px solid ${COLORS.grayscale300};
  outline: none;

  &:focus {
    border-color: ${COLORS.primary300};
  }

  &:disabled {
    color: ${COLORS.grayscale200};
    border-color: ${COLORS.grayscale200};

    &::placeholder {
      color: ${COLORS.grayscale200};
    }
  }

  &::placeholder {
    color: ${COLORS.grayscale400};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 16px;
`;

export default SearchInput;
